import React, { useState, useEffect, useCallback } from 'react';
import {
  ShoppingCart, CheckCircle, XCircle, Loader2, RefreshCw,
  Phone, Hash, Clock, History
} from 'lucide-react';
import { API_URL } from '../../config';

// ============================================================
// ADMIN - Commandes de crédits
// MVola (manuel) : vérifier la réf → Valider = crédits appliqués AUTO
// Stripe / Orange : déjà validées automatiquement (historique)
// ============================================================

const authHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
  'Content-Type': 'application/json'
});

const METHOD_BADGES = {
  mvola:        { label: '📱 MVola',   color: 'bg-yellow-500/20 text-yellow-300' },
  orange_money: { label: '🍊 Orange',  color: 'bg-orange-500/20 text-orange-300' },
  stripe:       { label: '💳 Stripe',  color: 'bg-indigo-500/20 text-indigo-300' },
};
const STATUS_BADGES = {
  pending:   { label: 'En attente', color: 'bg-yellow-500/20 text-yellow-300' },
  validated: { label: 'Validée ✓',  color: 'bg-green-500/20 text-green-300' },
  rejected:  { label: 'Rejetée',    color: 'bg-red-500/20 text-red-300' },
};

export default function CreditOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [message, setMessage] = useState(null);

  const load = useCallback(async (history = showHistory) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/bot/credit-orders?status=${history ? 'all' : 'pending'}`, {
        headers: authHeaders()
      });
      const data = await res.json();
      if (data.success) setOrders(data.orders);
    } finally { setLoading(false); }
  }, [showHistory]);

  useEffect(() => { load(); }, [load]);

  const validate = async (order) => {
    if (!window.confirm(`Valider le paiement ${order.payment_ref || ''} de ${order.user_email} (${(order.amount_ar || 0).toLocaleString()} Ar) ?\n\nLes crédits seront ajoutés AUTOMATIQUEMENT.`)) return;
    setBusyId(order.id);
    setMessage(null);
    try {
      const res = await fetch(`${API_URL}/api/bot/credit-orders/${order.id}/validate`, {
        method: 'POST', headers: authHeaders()
      });
      const data = await res.json();
      setMessage({ type: data.success ? 'ok' : 'error', text: data.message });
      load();
    } finally { setBusyId(null); }
  };

  const reject = async (order) => {
    const note = window.prompt('Raison du rejet (visible en interne) :', 'Paiement non retrouvé');
    if (note === null) return;
    setBusyId(order.id);
    try {
      await fetch(`${API_URL}/api/bot/credit-orders/${order.id}/reject`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify({ note })
      });
      load();
    } finally { setBusyId(null); }
  };

  const pendingCount = orders.filter(o => o.status === 'pending').length;

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white mb-1 flex items-center gap-3">
            <ShoppingCart className="w-8 h-8 text-green-400" />
            Commandes Crédits
            {pendingCount > 0 && (
              <span className="px-3 py-1 rounded-full bg-yellow-500 text-black text-sm font-bold animate-pulse">
                {pendingCount} à valider
              </span>
            )}
          </h1>
          <p className="text-sm text-blue-200">
            MVola : vérifiez la référence puis Valider → crédits appliqués automatiquement. Stripe/Orange : automatique.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => { setShowHistory(!showHistory); }}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
              showHistory ? 'bg-purple-600 text-white' : 'bg-white/10 text-gray-300 hover:bg-white/20'
            }`}
          >
            <History className="w-4 h-4" /> {showHistory ? 'Tout' : 'En attente'}
          </button>
          <button onClick={() => load()} className="p-2.5 rounded-xl bg-white/10 hover:bg-white/20 text-white">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {message && (
        <div className={`p-4 rounded-2xl flex items-center gap-3 text-sm ${
          message.type === 'ok'
            ? 'bg-green-500/10 border border-green-500/30 text-green-300'
            : 'bg-red-500/10 border border-red-500/30 text-red-300'
        }`}>
          {message.type === 'ok' ? <CheckCircle className="w-5 h-5" /> : <XCircle className="w-5 h-5" />}
          {message.text}
        </div>
      )}

      {loading ? (
        <div className="py-16 text-center"><Loader2 className="w-10 h-10 text-blue-400 animate-spin mx-auto" /></div>
      ) : orders.length === 0 ? (
        <div className="bg-white/5 border border-white/10 rounded-2xl p-10 text-center">
          <CheckCircle className="w-12 h-12 text-green-500/50 mx-auto mb-3" />
          <p className="text-gray-400">
            {showHistory ? 'Aucune commande de crédits.' : 'Aucune commande en attente — tout est validé ! 🎉'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {orders.map(order => {
            const method = METHOD_BADGES[order.payment_method] || { label: order.payment_method, color: 'bg-gray-500/20 text-gray-300' };
            const st = STATUS_BADGES[order.status] || STATUS_BADGES.pending;
            return (
              <div key={order.id} className="bg-white/5 border border-white/10 rounded-2xl p-4 md:p-5">
                <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <span className="text-white font-semibold text-sm">{order.user_email || order.user_id}</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${method.color}`}>{method.label}</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${st.color}`}>{st.label}</span>
                      <span className="text-gray-500 text-xs flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {order.created_at ? new Date(order.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                      </span>
                    </div>

                    {/* Panier */}
                    <div className="flex flex-wrap gap-2 mb-2">
                      {order.cart.map((item, i) => (
                        <span key={i} className="px-2.5 py-1 rounded-lg bg-white/10 text-blue-200 text-xs">
                          {item.serviceName || item.service} · {item.creditType === 'images' ? '📸' : '🎬'} {(item.quota || 0) * (item.quantity || 1)} {item.creditType}
                        </span>
                      ))}
                    </div>

                    <div className="flex items-center gap-4 text-xs text-gray-400 flex-wrap">
                      <span className="text-yellow-300 font-bold text-sm">{(order.amount_ar || 0).toLocaleString()} Ar</span>
                      {order.payment_ref && <span className="flex items-center gap-1"><Hash className="w-3 h-3" /> Réf : <code className="text-white">{order.payment_ref}</code></span>}
                      {order.client_phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {order.client_phone}</span>}
                    </div>
                    {order.note && <p className="text-gray-500 text-xs mt-1 italic">{order.note}</p>}
                  </div>

                  {order.status === 'pending' && (
                    <div className="flex gap-2 flex-shrink-0">
                      <button
                        onClick={() => validate(order)}
                        disabled={busyId === order.id}
                        className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-green-600 hover:bg-green-500 text-white font-bold text-sm transition-all disabled:opacity-50"
                      >
                        {busyId === order.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                        Valider → crédits auto
                      </button>
                      <button
                        onClick={() => reject(order)}
                        disabled={busyId === order.id}
                        className="px-3 py-2.5 rounded-xl bg-red-500/20 text-red-300 hover:bg-red-500/30 text-sm transition-all disabled:opacity-50"
                      >
                        <XCircle className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
